import { useState, useEffect } from 'react';
import { TOPICS } from '../data/topics';
import { fetchLesson } from '../api';
import { useLessonCache } from '../hooks/useLessonCache';
import LessonView from './LessonView';

// Check which subtopics have pre-authored PHAK content (no AI needed)
let bundledKeys = new Set();
let phakChaptersByTopic = {};
try {
  // eslint-disable-next-line
  const bundled = require('../data/lessons.json');
  bundledKeys = new Set(Object.keys(bundled));
  
  // Extract PHAK chapters organized by topic
  Object.entries(bundled).forEach(([key]) => {
    if (key.includes('PHAK')) {
      const [topic, title] = key.split('::');
      if (!phakChaptersByTopic[topic]) {
        phakChaptersByTopic[topic] = [];
      }
      phakChaptersByTopic[topic].push(title);
    }
  });
  
  // Sort chapters by chapter number
  Object.keys(phakChaptersByTopic).forEach(topic => {
    phakChaptersByTopic[topic].sort((a, b) => {
      const numA = parseInt(a.match(/Chapter (\d+)/)?.[1] || 0);
      const numB = parseInt(b.match(/Chapter (\d+)/)?.[1] || 0);
      return numA - numB;
    });
  });
} catch (_) {}

function isPHAK(topicKey, subtopic) {
  return bundledKeys.has(`${topicKey}::${subtopic}`);
}

function getPHAKChapters(topicKey) {
  return phakChaptersByTopic[topicKey] || [];
}

const LOAD_STEPS = [
  'Researching FAA content',
  'Writing lesson sections',
  'Creating ASCII diagrams',
  'Building glossary',
  'Generating quiz questions',
];

function LoadingLesson({ subtopic }) {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timers = LOAD_STEPS.map((_, i) =>
      setTimeout(() => setActiveStep(i), i * 800)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="lesson-loading">
      <div className="spinner" />
      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: 2, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 6 }}>
        Generating Lesson<span className="blink">_</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 18 }}>{subtopic?.split('—')[0].trim()}</div>
      <div className="loading-steps">
        {LOAD_STEPS.map((step, i) => (
          <div key={step} className={`loading-step ${i < activeStep ? 'done' : i === activeStep ? 'active' : ''}`}>
            {step}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LessonsPage({ initialTopicKey, initialSubtopic }) {
  const [activeTopic, setActiveTopic]     = useState(initialTopicKey || null);
  const [activeSubtopic, setActiveSubtopic] = useState(initialSubtopic || null);
  const [lessonData, setLessonData]       = useState(null);
  const [fromCache, setFromCache]         = useState(false);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [isBundled, setIsBundled]         = useState(false);
  const cache = useLessonCache();

  // Handle incoming navigation from Dashboard / sidebar
  useEffect(() => {
    if (initialTopicKey) setActiveTopic(initialTopicKey);
    if (initialSubtopic) loadLesson(initialTopicKey, initialSubtopic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTopicKey, initialSubtopic]);

  const loadLesson = async (topicKey, subtopic, forceRefresh = false) => {
    setActiveTopic(topicKey);
    setActiveSubtopic(subtopic);
    setError(null);

    if (!forceRefresh) {
      const cached = cache.get(topicKey, subtopic);
      if (cached) {
        setLessonData(cached);
        setFromCache(true);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setLessonData(null);
    setFromCache(false);

    // PHAK/bundled lessons resolve synchronously — skip the loading screen
    const cacheKey = `${topicKey}::${subtopic}`;
    const bundled = bundledKeys.has(cacheKey);
    setIsBundled(bundled);

    try {
      const t = TOPICS[topicKey];
      const data = await fetchLesson(t.label, subtopic, topicKey);
      cache.set(topicKey, subtopic, data);
      setLessonData(data);
      setFromCache(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = () => {
    cache.clear(activeTopic, activeSubtopic);
    loadLesson(activeTopic, activeSubtopic, true);
  };

  const renderContent = () => {
    if (!activeTopic) {
      return (
        <div className="lesson-picker">
          <div className="picker-icon">🛩️</div>
          <div className="picker-title">Choose a Topic to Begin</div>
          <div className="picker-sub">Select any topic from the left — Claude will generate a complete lesson just for you.</div>
        </div>
      );
    }

    const t = TOPICS[activeTopic];

    if (!activeSubtopic) {
      const phakCount = t.subtopics.filter(s => isPHAK(activeTopic, s)).length;
      const phakChapters = getPHAKChapters(activeTopic);
      const totalPhak = phakCount + phakChapters.length;
      
      return (
        <div className="lesson-picker">
          <div className="picker-icon">{t.icon}</div>
          <div className="picker-title">{t.label}</div>
          <div className="picker-sub">
            {totalPhak > 0
              ? <><span style={{ color: 'var(--amber)' }}>📖 {totalPhak} PHAK lesson{totalPhak > 1 ? 's' : ''}</span> ready to read — no AI needed. Other lessons are generated by Claude on demand.</>
              : <>Choose a sub-topic below — Claude will generate a full lesson with diagrams, glossary &amp; quiz.</>
            }
          </div>
          
          {/* PHAK Chapters Section */}
          {phakChapters.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                📖 PHAK Chapters
              </div>
              <div className="subtopic-grid">
                {phakChapters.map((chapter) => (
                  <button key={chapter} className="subtopic-btn" onClick={() => loadLesson(activeTopic, chapter)}
                    style={{ borderColor: 'rgba(251,191,36,0.25)', background: 'rgba(251,191,36,0.05)' }}>
                    <div className="subtopic-btn-title">{chapter.split(' - ')[0]}</div>
                    <div className="subtopic-btn-sub" style={{ color: 'var(--amber)' }}>
                      {chapter.split(' - ')[1] || '📖 PHAK chapter'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Regular AI-Generated Subtopics */}
          {t.subtopics.length > 0 && (
            <div>
              {phakChapters.length > 0 && (
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
                  ✦ AI-Generated Topics
                </div>
              )}
              <div className="subtopic-grid">
                {t.subtopics.map((s) => {
                  const isPhak = isPHAK(activeTopic, s);
                  return (
                    <button key={s} className="subtopic-btn" onClick={() => loadLesson(activeTopic, s)}
                      style={isPhak ? { borderColor: 'rgba(251,191,36,0.25)', background: 'rgba(251,191,36,0.05)' } : {}}>
                      <div className="subtopic-btn-title">{s.split('—')[0].trim()}</div>
                      <div className="subtopic-btn-sub" style={isPhak ? { color: 'var(--amber)' } : {}}>
                        {isPhak
                          ? '📖 PHAK — ready to read'
                          : s.includes('—') ? s.split('—')[1].trim() : '✦ Click to generate with AI'
                        }
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (error) {
      return (
        <div className="lesson-loading">
          <div style={{ fontSize: 36, marginBottom: 14 }}>⚠️</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--red)', letterSpacing: 2, marginBottom: 8 }}>GENERATION FAILED</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>{error}</div>
          <button className="cta-btn" onClick={() => loadLesson(activeTopic, activeSubtopic, true)}>RETRY</button>
        </div>
      );
    }

    if (loading) return isBundled ? null : <LoadingLesson subtopic={activeSubtopic} />;

    if (lessonData) {
      return (
        <LessonView
          topicKey={activeTopic}
          subtopic={activeSubtopic}
          lessonData={lessonData}
          fromCache={fromCache}
          onRegenerate={handleRegenerate}
        />
      );
    }

    return null;
  };

  return (
    <div className="fade-up">
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Ground School Lessons</div>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>
          <span style={{ color: 'var(--amber)' }}>📖 PHAK</span> lessons are pre-authored from the FAA handbook. Other topics are <span style={{ color: 'var(--blue)' }}>✦ AI Generated</span> on demand.
        </div>
      </div>

      <div className="lessons-layout">
        {/* Topic sidebar */}
        <div className="lesson-sidebar-panel">
          <div className="lsp-header">Topics</div>
          {Object.entries(TOPICS).map(([key, t]) => {
            const phakCount = t.subtopics.filter(s => isPHAK(key, s)).length;
            const phakChapters = getPHAKChapters(key);
            const totalPhak = phakCount + phakChapters.length;
            return (
              <div
                key={key}
                className={`lsp-topic ${activeTopic === key ? 'active' : ''}`}
                onClick={() => { setActiveTopic(key); setActiveSubtopic(null); setLessonData(null); }}
              >
                <div className="lsp-topic-icon">{t.icon}</div>
                <div style={{ flex: 1 }}>
                  <div className="lsp-topic-name">{t.label.split('&')[0].trim()}</div>
                  <div className="lsp-topic-sub">
                    {totalPhak > 0
                      ? <><span style={{ color: 'var(--amber)' }}>📖 {totalPhak}</span> · {t.subtopics.length} sub-topics</>
                      : <>{t.subtopics.length} sub-topics</>
                    }
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Content area */}
        <div className="lesson-content-area">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
